import { useState } from "react";
import styles from "./CommunityContentPage.module.css";
import { ENDPOINTS } from "../../utils/endpoints";
import videoPlaceholderImage from "../../assets/images/video-player-placeholder.png";
import AttachmentLink from "./AttachmentLink";

const formatBoldText = (text = "") =>
  text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

const isMobileApp = () => /flutter|webview/i.test(navigator.userAgent);

// Normalize an API `sections` payload; when absent, wrap a legacy single
// content block (+ its video_tabs) into one synthetic section.
export const toSections = (contentData) => {
  if (!contentData) return [];
  if (contentData.sections && contentData.sections.length) {
    return contentData.sections;
  }
  const videoTabs = contentData.video_tabs || contentData.video_links || [];
  const hasLegacy =
    contentData.content_title ||
    contentData.content_primary_text ||
    contentData.content_secondary_text ||
    contentData.content_banner_web ||
    contentData.content_image_web ||
    videoTabs.length;
  if (!hasLegacy) return [];
  return [
    {
      section_title: contentData.content_title,
      section_primary_text: contentData.content_primary_text,
      section_secondary_text: contentData.content_secondary_text,
      banner_web: contentData.content_banner_web,
      banner_app: contentData.content_banner_app,
      image_web: contentData.content_image_web,
      image_app: contentData.content_image_app,
      video_tabs: videoTabs,
    },
  ];
};

// Videos whose plans are all expired (or expired for this user) are hidden.
const filterVisibleVideos = (videos, role, userMembershipIds) =>
  (videos || []).filter((video) => {
    if (role === "administrator") return true;
    const expiry = video.membership_expiry || [];
    if (expiry.length === 0) return true;
    const now = new Date();
    const allExpired = expiry.every(
      (e) => e.expires_at && new Date(e.expires_at) <= now,
    );
    if (allExpired) return false;
    const userRelevant = expiry.filter((e) =>
      userMembershipIds.includes(Number(e.membership_id)),
    );
    if (userRelevant.length === 0) return true;
    return !userRelevant.every(
      (e) => e.expires_at && new Date(e.expires_at) <= now,
    );
  });

// Elegant separator shown between stacked sections.
const SectionDivider = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      margin: "32px 0",
    }}
  >
    <div
      style={{
        flex: 1,
        height: "1px",
        background:
          "linear-gradient(to right, transparent, rgba(0,0,0,0.18))",
      }}
    />
    <span style={{ color: "rgba(0,0,0,0.3)", fontSize: "16px", letterSpacing: "4px" }}>
      •••
    </span>
    <div
      style={{
        flex: 1,
        height: "1px",
        background: "linear-gradient(to left, transparent, rgba(0,0,0,0.18))",
      }}
    />
  </div>
);

const SectionBlock = ({
  section,
  sectionIndex,
  thumbnailCache,
  role,
  userMembershipIds,
  onVideoClick,
  onAttachmentClick,
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const bannerImage = isMobileApp() ? section.banner_app : section.banner_web;
  const contentImage = isMobileApp() ? section.image_app : section.image_web;
  const videoTabs = section.video_tabs || [];
  const currentTab = videoTabs[activeTabIndex];
  const groups = currentTab?.groups || [];

  return (
    <div className={styles.sectionBlock}>
      {sectionIndex > 0 && <SectionDivider />}
      {bannerImage && (
        <img
          src={ENDPOINTS.IMAGE_BASE_URL + bannerImage}
          className={styles.bannerImage}
          alt="Banner"
        />
      )}
      {section.section_title && (
        <h2
          className={styles.subtitle}
          dangerouslySetInnerHTML={{
            __html: formatBoldText(section.section_title),
          }}
        />
      )}
      {section.section_primary_text && (
        <div
          className={styles.descriptionContainer}
          dangerouslySetInnerHTML={{
            __html: formatBoldText(section.section_primary_text),
          }}
        />
      )}
      {contentImage && (
        <img
          src={ENDPOINTS.IMAGE_BASE_URL + contentImage}
          className={styles.bannerImage}
          alt="Content"
        />
      )}
      {section.section_secondary_text && (
        <div
          className={styles.descriptionContainer}
          dangerouslySetInnerHTML={{
            __html: formatBoldText(section.section_secondary_text),
          }}
        />
      )}

      {videoTabs.length > 0 && (
        <div className={styles.videoSection}>
          <div className={styles.tabsContainer}>
            {videoTabs.map((tab, index) => (
              <button
                key={index}
                className={`${styles.tabButton} ${activeTabIndex === index ? styles.activeTab : ""}`}
                onClick={() => setActiveTabIndex(index)}
              >
                {tab.tab_name}
              </button>
            ))}
          </div>

          <div className={styles.groupsContainer}>
            {groups.map((group, groupIndex) => (
              <div key={groupIndex} className={styles.videoGroup}>
                <h3 className={styles.groupTitle}>{group.group_title}</h3>

                {isMobileApp() && group.group_banner_mobile_url && (
                  <img
                    src={ENDPOINTS.IMAGE_BASE_URL + group.group_banner_mobile_url}
                    className={styles.bannerImage}
                    alt="Content"
                  />
                )}
                {!isMobileApp() && group.group_banner_web_url && (
                  <img
                    src={ENDPOINTS.IMAGE_BASE_URL + group.group_banner_web_url}
                    className={styles.bannerImage}
                    alt="Content"
                  />
                )}

                {group.group_description && (
                  <p className={styles.groupDescription}>
                    {group.group_description}
                  </p>
                )}

                <div className={styles.videoGrid}>
                  {filterVisibleVideos(group.videos, role, userMembershipIds).map(
                    (video, videoIndex) => {
                      const thumbnailKey = video.id || video.url;
                      let videoImage = videoPlaceholderImage;
                      if (video.cover_image) {
                        videoImage = ENDPOINTS.IMAGE_BASE_URL + video.cover_image;
                      } else if (thumbnailCache[thumbnailKey]) {
                        videoImage = thumbnailCache[thumbnailKey];
                      }

                      return (
                        <div
                          key={videoIndex}
                          className={styles.videoCard}
                          onClick={() => onVideoClick(video)}
                        >
                          <div className={styles.videoThumbnailWrapper}>
                            <img
                              src={videoImage}
                              className={styles.videoThumbnail}
                              style={{ objectFit: "cover" }}
                              alt={video.title || "Video"}
                            />
                            <div className={styles.videoOverlay}>
                              <div className={styles.playIcon}>▶</div>
                            </div>
                          </div>
                          {video.title && (
                            <div className={styles.videoTitle}>{video.title}</div>
                          )}
                          {video.attach_file && (
                            <AttachmentLink
                              fileUrl={ENDPOINTS.IMAGE_BASE_URL + video.attach_file}
                              video={video}
                              onClick={onAttachmentClick}
                            />
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PageContentSections = ({
  sections,
  thumbnailCache,
  role,
  userMembershipIds,
  onVideoClick,
  onAttachmentClick,
}) => {
  return (
    <>
      {sections.map((section, index) => (
        <SectionBlock
          key={section.id ?? index}
          section={section}
          sectionIndex={index}
          thumbnailCache={thumbnailCache}
          role={role}
          userMembershipIds={userMembershipIds}
          onVideoClick={onVideoClick}
          onAttachmentClick={onAttachmentClick}
        />
      ))}
    </>
  );
};

export default PageContentSections;
